import apiClient from './apiClient';

export interface User {
  id: string;
  userCode?: string;
  name?: string; // For guest customers
  email?: string; // For guest customers (top-level)
  phone?: string; // For guest customers (top-level)
  isGuest?: boolean; // Flag for unregistered guest customers
  isVerified?: boolean; // Verification status
  personalInfo: {
    firstName: string;
    lastName: string;
    email: string | undefined;
    phone: string | undefined;
    dateOfBirth?: string;
    profilePicture?: string;
  };
  accountInfo: {
    status: 'active' | 'suspended' | 'pending' | 'banned';
    role: 'customer' | 'driver' | 'admin' | 'host';
    createdAt: string;
    lastLoginAt?: string;
    emailVerified: boolean;
    phoneVerified: boolean;
    kycStatus?: 'pending' | 'verified' | 'rejected';
  };
  activitySummary?: {
    totalBookings: number;
    totalSpent: number;
    averageRating?: number;
    completedTrips: number;
    cancelledTrips: number;
    lastActivity?: string;
  };
  preferences?: {
    notifications: {
      email: boolean;
      sms: boolean;
      push: boolean;
    };
    language: string;
    currency: string;
    timezone: string;
  };
  verification?: {
    identityStatus: 'pending' | 'verified' | 'rejected';
    licenseStatus?: 'pending' | 'verified' | 'rejected';
    backgroundCheckStatus?: 'pending' | 'verified' | 'rejected';
    documents?: Array<{
      type: string;
      url: string;
      status: 'pending' | 'verified' | 'rejected';
      uploadedAt: string;
    }>;
  };
}

export interface UserFilters {
  search?: string;
  role?: string;
  status?: string;
  verificationStatus?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface UserResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

class UserService {
  private baseUrl = '/api/v1/users';
  private adminBaseUrl = '/api/v1/admin/users';

  // Get all users with filtering and pagination (admin endpoint with verification data)
  async getUsers(filters: UserFilters = {}): Promise<UserResponse> {
    try {

      const params = new URLSearchParams();

      // Admin endpoint supported filters
      if (filters.search) params.append('search', filters.search);
      if (filters.limit) params.append('limit', filters.limit.toString());

      // NOTE: Not using userType filter due to backend bug
      // Backend returns empty array when userType=renter is set
      // We'll filter by role on the frontend instead
      // TODO: Re-enable once backend fixes userType filter

      // Use isVerified filter for verification status (true/false)
      if (filters.verificationStatus === 'verified') {
        params.append('isVerified', 'true');
      } else if (filters.verificationStatus === 'unverified') {
        params.append('isVerified', 'false');
      }

      const response = await apiClient.get(`${this.adminBaseUrl}?${params.toString()}`);


      // Handle admin endpoint response format: { success: true, data: { users: [...], statistics: {...} } }
      if (response.data.success && response.data.data?.users) {
        let users = response.data.data.users;

        // Client-side filtering by role/userType (backend filter is broken)
        if (filters.role) {
          const targetUserType = filters.role === 'customer' ? 'renter' : filters.role;
          users = users.filter((user: any) => user.userType === targetUserType);
        }

        return {
          users: users,
          total: users.length,
          page: 1,
          limit: filters.limit || users.length,
          totalPages: 1
        };
      } else if (response.data.users) {
        return response.data;
      } else if (Array.isArray(response.data)) {
        return {
          users: response.data,
          total: response.data.length,
          page: 1,
          limit: response.data.length,
          totalPages: 1
        };
      } else {
        throw new Error('Invalid response format from backend');
      }
    } catch (error) {
      console.error('❌ Failed to fetch users:', error);
      throw error;
    }
  }

  // Get single user by ID
  async getUserById(userId: string): Promise<User> {
    try {
      
      const response = await apiClient.get(`${this.baseUrl}/${userId}`);
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch user:', error);
      throw error;
    }
  }

  // Update user
  async updateUser(userId: string, userData: Partial<User>): Promise<User> {
    try {
      
      const response = await apiClient.put(`${this.baseUrl}/${userId}`, userData);
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to update user:', error);
      throw error;
    }
  }

  // Suspend/unsuspend user
  async updateUserStatus(userId: string, status: string, reason?: string): Promise<User> {
    try {
      
      const response = await apiClient.patch(`${this.baseUrl}/${userId}/status`, {
        status,
        reason
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to update user status:', error);
      throw error;
    }
  }

  // Get user statistics
  async getUserStats(): Promise<any> {
    try {

      const response = await apiClient.get(`${this.baseUrl}/stats`);

      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch user stats:', error);

      // Return mock stats as fallback
      return {
        totalUsers: 0,
        activeUsers: 0,
        newUsersToday: 0,
        verifiedUsers: 0,
        suspendedUsers: 0
      };
    }
  }

  // Get user notification preferences (admin endpoint)
  async getNotificationPreferences(userId: string): Promise<{
    success: boolean;
    data?: {
      userId: string;
      userName: string;
      email: string;
      phone: string;
      hasPushToken: boolean;
      pushPlatform?: string;
      preferences: {
        push: boolean;
        email: boolean;
        sms: boolean;
      };
    };
    error?: string;
  }> {
    try {
      const response = await apiClient.get(`${this.adminBaseUrl}/${userId}/notification-preferences`);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch notification preferences:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch notification preferences'
      };
    }
  }

  // Update user notification preferences (admin endpoint)
  async updateNotificationPreferences(
    userId: string,
    preferences: {
      push?: boolean;
      email?: boolean;
      sms?: boolean;
    },
    reason?: string
  ): Promise<{
    success: boolean;
    message?: string;
    data?: {
      userId: string;
      preferences: {
        push: boolean;
        email: boolean;
        sms: boolean;
      };
      changes: string[];
    };
    error?: string;
  }> {
    try {
      const response = await apiClient.put(`${this.adminBaseUrl}/${userId}/notification-preferences`, {
        ...preferences,
        reason
      });
      return response.data;
    } catch (error) {
      console.error('❌ Failed to update notification preferences:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update notification preferences'
      };
    }
  }

}

export default new UserService();